import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#3949ab' : '#e0e0e0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(26, 35, 126, 0.1)' : 'none',
    '&:hover': { borderColor: '#3949ab' },
    fontSize: 14,
    minHeight: 38,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected ? '#1a237e' : state.isFocused ? '#e8eaf6' : 'white',
  }),
  placeholder: (base) => ({ ...base, color: '#9e9e9e' }),
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
};

const ALL_METHODS = [
  {
    key: 'DIRECT_UPLOAD',
    title: 'File Upload',
    icon: '📤',
    description: 'Upload one or more PDF / DOCX files (max 5 MB each).',
    needsTemplate: false,
    flag: 'allowDirectUpload',
  },
  {
    key: 'MANUAL_GENERATION',
    title: 'Manual Generation',
    icon: '✍️',
    description: 'Pick a template and author each question in a rich editor.',
    needsTemplate: true,
    flag: 'allowManualGeneration',
  },
  {
    key: 'AUTO_GENERATION',
    title: 'Auto-Generate',
    icon: '⚡',
    description: 'Pick a template and populate questions from the Question Bank. Editable before submit; re-runnable.',
    needsTemplate: true,
    flag: 'allowAutoGeneration',
  },
];

function MethodPickerModal({ isOpen, onClose, onConfirm, methods, templates, row }) {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedMethod(null);
      setSelectedTemplate(null);
      setSaving(false);
    }
  }, [isOpen]);

  const availableMethods = useMemo(() => {
    return ALL_METHODS.filter((m) => methods?.[m.flag] !== false);
  }, [methods]);

  const templateOptions = useMemo(() => {
    return (templates || []).map((t) => ({ value: t.TemplateID, label: t.TemplateName }));
  }, [templates]);

  if (!isOpen) return null;

  const chosen = availableMethods.find((m) => m.key === selectedMethod) || null;
  const canConfirm = !!chosen && (!chosen.needsTemplate || !!selectedTemplate) && !saving;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSaving(true);
    try {
      await onConfirm({
        generationMethod: selectedMethod,
        templateId: chosen.needsTemplate ? selectedTemplate.value : null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="qpg-modal-backdrop" onClick={onClose}>
      <div className="qpg-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qpg-modal-header">
          <div>
            <h3>Start Paper Generation</h3>
            {row && (
              <div className="qpg-modal-sub">
                {row.courseCode} — {row.courseName} · {row.examTypeName} · Set {row.setName}
              </div>
            )}
          </div>
          <button className="qpg-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="qpg-modal-body">
          {availableMethods.length === 0 ? (
            <div className="qpg-empty">No generation methods are enabled in QP Settings.</div>
          ) : (
            <>
              <div className="qpg-method-cards">
                {availableMethods.map((m) => (
                  <button
                    type="button"
                    key={m.key}
                    className={`qpg-method-card ${selectedMethod === m.key ? 'selected' : ''}`}
                    onClick={() => { setSelectedMethod(m.key); setSelectedTemplate(null); }}
                  >
                    <div className="qpg-method-icon">{m.icon}</div>
                    <div className="qpg-method-title">{m.title}</div>
                    <div className="qpg-method-desc">{m.description}</div>
                  </button>
                ))}
              </div>

              {chosen?.needsTemplate && (
                <div className="qpg-modal-field">
                  <label>Template</label>
                  <Select
                    options={templateOptions}
                    value={selectedTemplate}
                    onChange={setSelectedTemplate}
                    placeholder={templateOptions.length === 0 ? 'No templates available' : 'Select a template...'}
                    styles={selectStyles}
                    isDisabled={templateOptions.length === 0}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    isClearable
                  />
                  {templateOptions.length === 0 && (
                    <p className="qpg-hint qpg-hint-warn">
                      Create a template in QP Template Library before using this method.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="qpg-modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleConfirm} disabled={!canConfirm}>
            {saving ? 'Starting...' : 'Start'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MethodPickerModal;
